import { useApiPostMutation } from "@repo/api";
import type { CheckoutItem } from "@repo/api";
import { useToast, useTranslation, useAuthentication } from "@repo/core";

export const useStripeCheckout = () => {
    const { addErrorMessage } = useToast();
    const { t } = useTranslation();
    const { user } = useAuthentication();

    const mutation = useApiPostMutation<{ url?: string; checkoutUrl?: string }, { subscriptions: string[], successUrl: string, cancelUrl: string, email?: string, stripeCustomerId?: string }>({
        getPath: () => "/stripe/create-checkout-session",
        getConfig: (data) => ({
            body: data,
        }),
    });

    const createCheckoutSession = async (items: CheckoutItem[]) => {
        if (items.length === 0) {
            addErrorMessage(t('subscriptions.selectProductError'));
            return;
        }

        try {
            const result = await mutation.mutateAsync({
                subscriptions: items.map(item => item.priceId),
                successUrl: `${window.location.origin}/subscriptions?success=true`,
                cancelUrl: `${window.location.origin}/subscriptions?canceled=true`,
                email: user?.email || '',
                stripeCustomerId: user?.stripeCustomerId || '',
            });

            console.log('Checkout session result:', result);

            // Handle different response formats
            const redirectUrl = result?.url || result?.checkoutUrl;

            if (redirectUrl) {
                window.location.href = redirectUrl;
            } else {
                console.error('No redirect URL found in response:', result);
                addErrorMessage(t('subscriptions.checkoutError'));
            }
        } catch (error: any) {
            console.error('Checkout error:', error);
            addErrorMessage(t('subscriptions.checkoutError'));
        }
    };

    const customerPortalMutation = useApiPostMutation<{ url?: string; checkoutUrl?: string }, { successUrl: string, stripeCustomerId: string }>({
        getPath: () => "/stripe/create-customer-portal-session",
        getConfig: (data) => ({
            body: data,
        }),
    });

    const createCustomerPortalSession = async () => {
        try {
            const result = await customerPortalMutation.mutateAsync({
                successUrl: `${window.location.origin}`,
                stripeCustomerId: user?.stripeCustomerId || '',
            });

            console.log('Customer portal session result:', result);

            // Handle different response formats
            const redirectUrl = result?.url || result?.checkoutUrl;

            if (redirectUrl) {
                window.location.href = redirectUrl;
            } else {
                console.error('No redirect URL found in response:', result);
                addErrorMessage(t('subscriptions.checkoutError'));
            }
        } catch (error: any) {
            console.error('Customer portal session error:', error);
            addErrorMessage(t('subscriptions.checkoutError'));
        }
    };

    return {
        createCheckoutSession,
        createCustomerPortalSession,
        loading: mutation.isPending,
        customerPortalLoading: customerPortalMutation.isPending,
    };
};
